import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "../components/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import instance from "../api/axios";
import mixpanel from "../mixpanel";
import { getTherapistById, createInternalBooking } from "../api/route";
import EditFormModel from "../components/Models/editFormModel";
import ConfirmModel from "../components/Models/confirmModel";
import FailModel from "../components/Models/failModel";
import BackHeader from "../components/Home/BackHeader";
import StepIndicator from "../components/Home/StepIndicator";
import DiscountCard from "../components/SessionDetail/DiscountCard";
import AppointmentDetailsCard from "../components/SessionDetail/AppointmentDetailsCard";
import PatientInfoCard from "../components/SessionDetail/PatientInfoCard";
import NotesCard from "../components/SessionDetail/NotesCard";
import AddressCard from "../components/SessionDetail/AddressCard";
import SpecialInstructionCard from "../components/SessionDetail/SpecialInstructionCard";
import { usePartner } from "../context/PartnerContext";
import { DEFAULT_SESSION_FEE, getSessionFee } from "../utils/pricing";
import {
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import DoctorAssignedCard from "../components/SessionDetail/DoctorAssignedCard";

const BookingDetails = () => {
  const libraries = ["places"];
  const center = { lat: 19.076, lng: 72.8777 };

  const [address, setAddress] = useState("");
  const [floor, setFloor] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [therapist, setTherapist] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState("init");
  const [bookingData, setBookingData] = useState(null);
  const [openEditModel, setOpenEditModel] = useState(false);
  const [Fees, setFees] = useState(DEFAULT_SESSION_FEE);
  const [notes, setNotes] = useState("");

  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [showFailModel, setShowFailModel] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [user, setUser] = useState(null);
  const [marker, setMarker] = useState(center);
  const [autocomplete, setAutocomplete] = useState(null);
  const [specialInstruction, setSpecialInstruction] = useState("");

  const mapRef = useRef();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerSlug } = useParams();

  useEffect(() => {
    console.log("BookingDetails received state from previous page:", state);
  }, [state]);

  const { therapistId, timing, isBlockedSlot, therapistName } = state;
  const effectiveTherapistId = therapistId || timing?.therapist_id;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyBqKbHptbwiV5W2wsYy0J1G1x2AKk2aeRU",
    libraries,
  });

  const onLoad = useCallback((autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const location = place.geometry.location;
        setMarker({ lat: location.lat(), lng: location.lng() });
        setAddress(place.formatted_address);
      }
    }
  }, [autocomplete]);

  const { config } = usePartner() || {};

  useEffect(() => {
    const fetchTherapist = async () => {
      setLoadingPhase("init");
      setLoading(true);
      const userDetail = sessionStorage.getItem("user");
      const user = JSON.parse(userDetail);
      setUser(user);
      setFees(getSessionFee({ partnerSlug, config }));

      try {
        const response = await instance.get(`${getTherapistById}/${effectiveTherapistId}`);
        const fetched = response?.data?.therapist || {};
        setTherapist((prev) => ({ doctor_name: therapistName, ...prev, ...fetched }));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchTherapist();
  }, [config, effectiveTherapistId, partnerSlug, therapistName]);

  useEffect(() => {
    setButtonDisabled(address.trim() === "" || floor.trim() === "");
  }, [address, floor]);

  const handleConfirmAppointment = async () => {
    setLoadingPhase("confirm");
    setLoading(true);
    const userDetail = sessionStorage.getItem("user");
    const user = JSON.parse(userDetail);
    const fallbackDoctorName = therapist?.doctor_name || therapistName || "";
    const obj = {
      doctor_id: therapist.doctor_id || effectiveTherapistId,
      user_name: user.name,
      user_phone: user.phone,
      user_pincode: user.pinCode,
      user_address: `${floor} ${address}`,
      user_city: user.address,
      therapist_name: fallbackDoctorName,
      therapist_email: therapist.email_id,
      profileImage: therapist.profileImage,
      time: timing.time,
      fees: Fees,
      user_condition: user.pain_condition || "",
      notes: notes || "",
      special_instruction: specialInstruction || "",
      sendPatientSMS: config?.send_patient_sms ?? true,
      sendTherapistSMS: config?.send_therapist_sms ?? true,
      blocked_slot: isBlockedSlot ? true : false,
      booking_source: partnerSlug || "physica",
      partner_ID: config?.referralCode || config?.referral_code || "",
    };

    try {
      const response = await instance.post(createInternalBooking, obj);
      if (response?.data?.success) {
        const bookingData = response?.data?.bookingData;
        console.log("Booking data", response);
        mixpanel.track("Button Clicked - Confirm Appointment", {
          "Doctor ID": obj.doctor_id,
          "User Name": obj.user_name,
          "User Phone": obj.user_phone,
          "User Pincode": obj.user_pincode,
          "User Address": obj.user_address,
          "User City": obj.user_city,
          "Therapist Name": obj.therapist_name,
          "Therapist Email": obj.therapist_email,
          Time: obj.time,
          Fees: obj.fees,
          Success: true,
          "Booking Details": bookingData,
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "enhanced_conversion",
          email: user?.email || "",
          phone_number: user?.phone || "",
          first_name: user?.name?.split(" ")[0] || "",
          last_name: user?.name?.split(" ")[1] || "",
          city: user?.address || "",
          pincode: user?.pinCode || "",
          booking_id: bookingData?.booking_id || "",
          therapist_name: obj?.therapist_name || "",
        });
        console.log("Enhanced Conversion data pushed to GTM:", {
          name: user?.name,
          phone: user?.phone,
          city: user?.address,
        });

        navigate("/appointment-confirmation", { state: { bookingData } });
      } else {
        setErrorMessage(response?.data?.message);
        setShowFailModel(true);
        mixpanel.track("Failed - Confirm Appointment", {
          "Doctor ID": obj.doctor_id,
          "Error Message": response?.data?.message,
        });
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message);
      setShowFailModel(true);
      mixpanel.track("Error - Confirm Appointment", {
        "Doctor ID": obj.doctor_id,
        "Error Message": error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = new Date(timing.time).toLocaleDateString("en-us", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const hour = new Date(timing.time).toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  const handleCloseConfirmModel = () => setShowConfirmModel(false);
  const handleCloseFailModel = () => setShowFailModel(false);
  const handleBackButtonClick = () => navigate(-1);

  if (loadError) return "Error loading maps";
  if (!isLoaded || loading) {
    const loadingText =
      loadingPhase === "confirm"
        ? "Confirming your Appointment"
        : "Blocking your Slot ...";
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          zIndex: 1000,
          backdropFilter: "blur(2px)",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <style>
          {`
            .loader {
              width: 50px;
              --b: 8px;
              aspect-ratio: 1;
              border-radius: 50%;
              padding: 1px;
              background: conic-gradient(#0000 10%, #FE8235) content-box;
              -webkit-mask:
                repeating-conic-gradient(#0000 0deg, #000 1deg 20deg, #0000 21deg 36deg),
                radial-gradient(farthest-side, #0000 calc(100% - var(--b) - 1px), #000 calc(100% - var(--b)));
              -webkit-mask-composite: destination-in;
                      mask-composite: intersect;
              animation: l4 1s infinite steps(10);
            }
            @keyframes l4 {
              to { transform: rotate(1turn); }
            }
          `}
        </style>
        <div className="loader" />
        <div style={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}>
          {loadingText}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="min-h-screen md:px-0 flex justify-center px-0 py-0 md:py-6 md:px-4">
        <div
          className="w-full max-w-[700px] md:rounded-2xl md:shadow-lg md:bg-cover md:bg-no-repeat"
          style={{
            backgroundImage: `url(${require("../assets/images/BackgroundBig.png")})`,
          }}
        >
          <div className="max-w-[700px] mx-auto">
            <BackHeader onBack={handleBackButtonClick} />
            <StepIndicator step={3} totalSteps={4} header="Confirm Appointment" />
            <DiscountCard />
            <DoctorAssignedCard
              doctorName={`Dr. ${(therapist.doctor_name || therapistName || "").trim()}`}
              doctorImage={therapist.profileImage}
            />
            <AppointmentDetailsCard date={formattedDate} time={hour} price={`\u20B9${Fees}`} />
            <PatientInfoCard
              patientName={user?.name}
              patientPhone={user?.phone}
              pincode={user?.pinCode}
              condition={user?.pain_condition || "N/A"}
              showEdit={true}
              onEditClick={() => setOpenEditModel(true)}
            />
            <AddressCard
              address={address}
              setAddress={setAddress}
              floor={floor}
              setFloor={setFloor}
              buttonDisabled={buttonDisabled}
              onLoad={onLoad}
              onPlaceChanged={onPlaceChanged}
            />
            <SpecialInstructionCard
              specialInstruction={specialInstruction}
              setSpecialInstruction={setSpecialInstruction}
            />
            <NotesCard notes={notes} setNotes={setNotes} />

            <div className="mt-0 p-4 flex flex-col items-center">
              <div className="w-80 ">
                <Button
                  btnName="Confirm Appointment"
                  className={`w-full offer-gradient text-center text-white font-semibold text-lg px-14 py-2 my-1.5 rounded-xl ${buttonDisabled ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  onClick={handleConfirmAppointment}
                  disabled={buttonDisabled}
                />

                <Button
                  btnName="Edit Details"
                  className="w-full text-center border border-[var(--partner-primary)] text-[var(--partner-primary)] font-semibold text-lg px-14 py-2 my-1.5 rounded-xl bg-transparent"
                  onClick={setOpenEditModel}
                />
              </div>
            </div>

            {openEditModel && (
              <EditFormModel setopenEditModel={setOpenEditModel} />
            )}
            {showConfirmModel && bookingData && (
              <ConfirmModel
                onClose={handleCloseConfirmModel}
                bookingId={bookingData.booking_id}
                date={formattedDate}
                time={hour}
              />
            )}
            {showFailModel && (
              <FailModel onClose={handleCloseFailModel} errorMessage={errorMessage} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
